import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogOut, MessageSquare, Shield, Store, Users as UsersIcon, Menu, Award, Megaphone, UserPlus, AlertTriangle, ClipboardList, RefreshCw, CheckCircle2, XCircle, Wrench, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useAuth } from '../App';
import {
  AdminProvider,
  useAdminData,
  AdminBookingsTab,
  AdminPaymentsTab,
  AdminUsersTab,
  AdminFeedbackTab,
  AdminListingsTab,
  AdminDisputesTab,
  AdminReviewsTab,
  AdminProfileTab
} from '../components/admin';
import AdminInquiriesTab from '../components/admin/AdminInquiriesTab';
import AdminBadgesTab from '../components/admin/AdminBadgesTab';
import AdminBannersTab from '../components/admin/AdminBannersTab';
import AdminSignupsTab from '../components/admin/AdminSignupsTab';
import AdminAttentionTab from '../components/admin/AdminAttentionTab';
import AdminAuditLogsTab from '../components/admin/AdminAuditLogsTab';
import AdminToolsTab from '../components/admin/AdminToolsTab';
import AdminScenariosTab from '../components/admin/AdminScenariosTab';
import AdminEventTypes from '../components/AdminEventTypes';

// Inner component that uses the admin context
const AdminDashboardContent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { reviews, feedback, activeInquiries, loading } = useAdminData();
  
  // Get tab from URL params, default to 'attention' (priority view)
  const tabFromUrl = searchParams.get('tab') || 'attention';
  const searchFromUrl = searchParams.get('search') || '';
  
  const [activeTab, setActiveTab] = React.useState(tabFromUrl);
  const [unreadSignupsCount, setUnreadSignupsCount] = React.useState(0);
  const [attentionCount, setAttentionCount] = React.useState(0);
  const [migrationsEnabled, setMigrationsEnabled] = React.useState(true); // default true to avoid flash
  
  // Environment health state
  const [envHealth, setEnvHealth] = React.useState(null);
  const [envHealthLoading, setEnvHealthLoading] = React.useState(false);
  const [envHealthError, setEnvHealthError] = React.useState(null);

  // Sync tab changes with URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    // Clear search param when changing tabs (unless going to users)
    if (newTab !== 'users') {
      newParams.delete('search');
    }
    setSearchParams(newParams, { replace: true });
    // Scroll to top when changing tabs
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Handle URL param changes (e.g., from AdminSignupsTab navigation)
  React.useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const pendingReviewsCount = reviews.filter(r => !r.is_approved).length;
  const pendingFeedbackCount = feedback.filter(f => f.status === 'pending' || f.status === 'new').length;

  // Fetch unread signups count
  React.useEffect(() => {
    const fetchUnreadSignups = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/signup-notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadSignupsCount(data.unread_count || 0);
        }
      } catch (error) {
        console.error('Error fetching signup count:', error);
      }
    };
    fetchUnreadSignups();
  }, []);

  // Fetch attention needed count
  React.useEffect(() => {
    const fetchAttentionCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/attention-needed`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAttentionCount(data.counts?.total || 0);
        }
      } catch (error) {
        console.error('Error fetching attention count:', error);
      }
    };
    fetchAttentionCount();
  }, []);

  // Fetch platform settings to check migration flag
  React.useEffect(() => {
    const fetchMigrationFlag = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setMigrationsEnabled(data.admin_migrations_enabled ?? true);
        }
      } catch (error) {
        console.error('Error fetching platform settings:', error);
      }
    };
    fetchMigrationFlag();
  }, []);

  // Fetch environment health on mount
  const fetchEnvHealth = React.useCallback(async () => {
    setEnvHealthLoading(true);
    setEnvHealthError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/env-health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEnvHealth(data);
      } else {
        setEnvHealthError('Failed to fetch environment health');
      }
    } catch (error) {
      console.error('Error fetching env health:', error);
      setEnvHealthError('Network error');
    } finally {
      setEnvHealthLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchEnvHealth();
  }, [fetchEnvHealth]);

  // Get blocking issues as array of labels
  const blockingIssues = React.useMemo(() => {
    if (!envHealth?.blocking_issues) return [];
    const issues = [];
    const labels = {
      stripe_payments_disabled: 'Stripe payments disabled (STRIPE_SECRET_KEY missing)',
      webhook_verification_disabled: 'Webhook verification disabled (STRIPE_WEBHOOK_SECRET missing)',
      cookie_auth_broken: 'Cookie auth broken (CORS_ORIGINS is wildcard)',
      s3_uploads_disabled: 'S3 uploads disabled (AWS credentials missing)'
    };
    for (const [key, isBlocking] of Object.entries(envHealth.blocking_issues)) {
      if (isBlocking && labels[key]) {
        issues.push(labels[key]);
      }
    }
    return issues;
  }, [envHealth]);

  if (loading) {
    return (
      <div className="min-h-screen-safe bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-4">
          <div className="flex justify-between items-center">
            <div 
              className="flex items-center space-x-2 sm:space-x-3 cursor-pointer flex-shrink-0"
              onClick={() => navigate('/')}
            >
              <img 
                src="/celeyapp-icon.png" 
                alt="CeleyApp" 
                className="h-6 w-6 sm:h-8 sm:w-8" 
                style={{ filter: 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)' }}
              />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">CeleyApp</h1>
                <p className="text-xs text-gray-500">Admin Dashboard</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              <span className="text-sm text-gray-600">Welcome, {user?.name}</span>
              <Button onClick={() => navigate('/admin/settings')} variant="outline" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                Platform Settings
              </Button>
              <Button onClick={() => navigate('/admin/concierge')} variant="outline" data-testid="admin-nav-concierge">
                Concierge
              </Button>
              <Button onClick={() => navigate('/contact')} variant="ghost">
                Contact
              </Button>
              <Button onClick={logout} variant="ghost">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px]">
                  <div className="flex flex-col space-y-4 mt-8">
                    <div className="py-2 px-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Logged in as</p>
                      <p className="font-medium text-gray-900">{user?.name}</p>
                    </div>
                    <Button variant="outline" className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => navigate('/admin/settings')}>
                      Platform Settings
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate('/admin/concierge')}
                      data-testid="admin-nav-concierge-mobile"
                    >
                      Concierge
                    </Button>
                    <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/contact')}>
                      Contact
                    </Button>
                    <Button variant="ghost" className="w-full justify-start" onClick={logout}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* Migration Warning Banner - shown only when migrations are disabled */}
      {!migrationsEnabled && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-2">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>Admin migrations are disabled in this environment.</span>
            </div>
          </div>
        </div>
      )}

      {/* Environment Health Banner - shown when blocking issues exist */}
      {blockingIssues.length > 0 && (
        <div className="bg-red-50 border-b border-red-200" data-testid="env-health-banner">
          <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-3">
            <div className="flex flex-col sm:flex-row sm:items-start gap-2">
              <div className="flex items-center gap-2 text-red-800 flex-shrink-0">
                <XCircle className="h-5 w-5" />
                <span className="font-semibold text-sm">Environment Configuration Issues</span>
              </div>
              <div className="flex-1">
                <ul className="text-red-700 text-sm space-y-1 ml-7 sm:ml-0">
                  {blockingIssues.map((issue, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <span className="text-red-400">•</span>
                      <span>{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchEnvHealth}
                disabled={envHealthLoading}
                className="border-red-300 text-red-700 hover:bg-red-100 flex-shrink-0"
                data-testid="env-health-refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${envHealthLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            {envHealth?.is_production === false && (
              <p className="text-red-600 text-xs mt-2 ml-7">
                Note: Running in development mode. Set ENV=production and configure secrets in dashboard for production.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Environment OK Badge - shown when no blocking issues */}
      {envHealth && blockingIssues.length === 0 && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="env-health-ok">
                <CheckCircle2 className="h-4 w-4" />
                <span>Environment configured correctly</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchEnvHealth}
                disabled={envHealthLoading}
                className="text-emerald-600 hover:text-emerald-700 h-6 px-2"
              >
                <RefreshCw className={`h-3 w-3 ${envHealthLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-8 xl:px-12 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-4 sm:grid-cols-17 gap-1 mb-4 sm:mb-6 h-auto p-1">
            <TabsTrigger value="attention" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 text-amber-500" />
              <span className="hidden sm:inline ml-1">Attention</span>
              {attentionCount > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-amber-500 text-xs px-1">{attentionCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="reviews" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <span className="hidden sm:inline">Reviews</span>
              <span className="sm:hidden">Rev</span>
              {pendingReviewsCount > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-red-500 text-xs px-1">{pendingReviewsCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="feedback" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <span className="hidden sm:inline">Feedback</span>
              <span className="sm:hidden">Feed</span>
              {pendingFeedbackCount > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-red-500 text-xs px-1">{pendingFeedbackCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="active-inquiries" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <span className="hidden sm:inline">Active</span>
              <span className="sm:hidden">Act</span>
              {activeInquiries.length > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-indigo-500 text-xs px-1">{activeInquiries.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <UsersIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="signups" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Signups</span>
              {unreadSignupsCount > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-emerald-500 text-xs px-1">{unreadSignupsCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="listings" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <Store className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Listings</span>
            </TabsTrigger>
            <TabsTrigger value="badges" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <Award className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Badges</span>
            </TabsTrigger>
            <TabsTrigger value="banners" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <Megaphone className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Banners</span>
            </TabsTrigger>
            <TabsTrigger value="disputes" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Disputes</span>
            </TabsTrigger>
            <TabsTrigger value="bookings" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              Bookings
            </TabsTrigger>
            <TabsTrigger value="payments" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              Payments
            </TabsTrigger>
            <TabsTrigger value="profile" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <Shield className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="audit-logs" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <ClipboardList className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Audit Logs</span>
            </TabsTrigger>
            <TabsTrigger value="event-types" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <span className="hidden sm:inline">Event Types</span>
              <span className="sm:hidden">Types</span>
            </TabsTrigger>
            <TabsTrigger value="tools" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <Wrench className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Tools</span>
            </TabsTrigger>
            <TabsTrigger value="scenarios" className="text-xs sm:text-sm py-2 px-1 sm:px-3">
              <FlaskConical className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">Scenarios</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab Contents - Each tab is now its own component */}
          <TabsContent value="attention">
            <AdminAttentionTab />
          </TabsContent>

          <TabsContent value="reviews">
            <AdminReviewsTab />
          </TabsContent>

          <TabsContent value="feedback">
            <AdminFeedbackTab />
          </TabsContent>

          <TabsContent value="active-inquiries">
            <AdminInquiriesTab />
          </TabsContent>

          <TabsContent value="users">
            <AdminUsersTab />
          </TabsContent>

          <TabsContent value="signups">
            <AdminSignupsTab />
          </TabsContent>

          <TabsContent value="listings">
            <AdminListingsTab />
          </TabsContent>

          <TabsContent value="badges">
            <AdminBadgesTab />
          </TabsContent>

          <TabsContent value="banners">
            <AdminBannersTab />
          </TabsContent>

          <TabsContent value="disputes">
            <AdminDisputesTab />
          </TabsContent>

          <TabsContent value="bookings">
            <AdminBookingsTab />
          </TabsContent>

          <TabsContent value="payments">
            <AdminPaymentsTab />
          </TabsContent>

          <TabsContent value="profile">
            <AdminProfileTab />
          </TabsContent>

          <TabsContent value="audit-logs">
            <AdminAuditLogsTab />
          </TabsContent>

          <TabsContent value="event-types">
            <AdminEventTypes />
          </TabsContent>

          <TabsContent value="tools">
            <AdminToolsTab />
          </TabsContent>

          <TabsContent value="scenarios">
            <AdminScenariosTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Main component that wraps everything in AdminProvider
const AdminDashboard = () => {
  return (
    <AdminProvider>
      <AdminDashboardContent />
    </AdminProvider>
  );
};

export default AdminDashboard;
